import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { CircularProgress } from "@material-ui/core";

import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
	formatMessageCalendarDay,
	formatMessageClock,
} from "../../helpers/dateTimeFormat";
import { useBusinessTimezone } from "../../context/BusinessTimezone/BusinessTimezoneContext";
import {
	TICKET_AI_REPORT_CREATED,
} from "../../helpers/ticketAiReportEvents";

function parseProblems(problems) {
	if (Array.isArray(problems)) {
		return problems.map((p) => String(p || "").trim()).filter(Boolean);
	}
	if (typeof problems === "string" && problems.trim()) {
		try {
			const parsed = JSON.parse(problems);
			if (Array.isArray(parsed)) {
				return parsed.map((p) => String(p || "").trim()).filter(Boolean);
			}
		} catch {
			return problems
				.split(/\n|;/)
				.map((s) => s.replace(/^[-*•\d.)\s]+/, "").trim())
				.filter(Boolean);
		}
	}
	return [];
}

function ReportCard({ report, timeZone }) {
	const problems = parseProblems(report.problems);
	const whenDay = formatMessageCalendarDay(report.createdAt, timeZone);
	const whenClock = formatMessageClock(report.createdAt, timeZone);

	return (
		<article
			className={clsx(
				"rounded-2xl border px-4 py-3 shadow-sm",
				report.isCurrentTicket
					? "border-primary/30 bg-primary/5 dark:border-primary/40 dark:bg-primary/10"
					: "border-default-200 bg-white dark:border-default-700 dark:bg-default-900"
			)}
		>
			<header className="mb-3 flex flex-wrap items-center gap-2">
				<span
					className={clsx(
						"inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
						report.isCurrentTicket
							? "bg-primary text-white"
							: "bg-default-200 text-default-700 dark:bg-default-700 dark:text-default-100"
					)}
				>
					{i18n.t("messagesInput.aiReport.ticketBadge", {
						id: report.ticketId,
					})}
				</span>
				{report.isCurrentTicket ? (
					<span className="text-[11px] font-medium text-primary">
						{i18n.t("messagesInput.aiReport.currentTicket")}
					</span>
				) : null}
				<span className="ms-auto text-[11px] text-default-500 dark:text-default-400">
					{whenDay} · {whenClock}
				</span>
			</header>

			<section className="mb-3">
				<h4 className="m-0 mb-1 text-xs font-semibold uppercase tracking-wide text-default-400">
					{i18n.t("messagesInput.aiReport.summary")}
				</h4>
				<p className="m-0 whitespace-pre-wrap text-sm leading-6 text-default-800 dark:text-default-100">
					{report.summary}
				</p>
			</section>

			<section className="mb-3">
				<h4 className="m-0 mb-1 text-xs font-semibold uppercase tracking-wide text-default-400">
					{i18n.t("messagesInput.aiReport.problems")}
				</h4>
				{problems.length ? (
					<ul className="m-0 list-disc space-y-1 ps-5 text-sm text-default-800 dark:text-default-100">
						{problems.map((item, idx) => (
							<li key={`${report.id}-p-${idx}`}>{item}</li>
						))}
					</ul>
				) : (
					<p className="m-0 text-sm text-default-500">
						{i18n.t("messagesInput.aiReport.noProblems")}
					</p>
				)}
			</section>

			<section className="mb-3">
				<h4 className="m-0 mb-1 text-xs font-semibold uppercase tracking-wide text-default-400">
					{i18n.t("messagesInput.aiReport.emotion")}
				</h4>
				<p className="m-0 text-sm font-semibold capitalize text-default-800 dark:text-default-100">
					{report.emotion || i18n.t("messagesInput.aiReport.emotionUnknown")}
				</p>
				{report.emotionDetail ? (
					<p className="mt-1 mb-0 text-sm leading-6 text-default-600 dark:text-default-300">
						{report.emotionDetail}
					</p>
				) : null}
			</section>

			<section>
				<h4 className="m-0 mb-1 text-xs font-semibold uppercase tracking-wide text-default-400">
					{i18n.t("messagesInput.aiReport.conclusion")}
				</h4>
				<p className="m-0 whitespace-pre-wrap text-sm leading-6 text-default-800 dark:text-default-100">
					{report.conclusion}
				</p>
			</section>

			{report.createdByName ? (
				<footer className="mt-3 border-t border-default-200 pt-2 text-[11px] text-default-400 dark:border-default-700">
					{i18n.t("messagesInput.aiReport.generatedBy", {
						name: report.createdByName,
					})}
				</footer>
			) : null}
		</article>
	);
}

export default function TicketAiReportsList({
	ticketId,
	tailwickLayout = false,
	onReportsChange,
}) {
	const { timeZone: businessTimeZone } = useBusinessTimezone();
	const [reports, setReports] = useState([]);
	const [loading, setLoading] = useState(true);

	const fetchReports = useCallback(async () => {
		if (!ticketId) {
			setReports([]);
			onReportsChange?.(false);
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			const { data } = await apiV2.get(`/tickets/${ticketId}/ai-reports`);
			const list = Array.isArray(data?.reports) ? data.reports : [];
			setReports(list);
			onReportsChange?.(list.length > 0);
		} catch (err) {
			console.error(err);
			toastError(err);
			setReports([]);
			onReportsChange?.(false);
		} finally {
			setLoading(false);
		}
	}, [ticketId, onReportsChange]);

	useEffect(() => {
		fetchReports();
	}, [fetchReports]);

	useEffect(() => {
		const onCreated = (event) => {
			const detail = event?.detail;
			if (!detail) return;
			if (Number(detail.ticketId) !== Number(ticketId)) return;
			if (detail.report?.id) {
				setReports((prev) => {
					const without = prev.filter((r) => r.id !== detail.report.id);
					const next = [detail.report, ...without];
					onReportsChange?.(next.length > 0);
					return next;
				});
			} else {
				fetchReports();
			}
		};
		window.addEventListener(TICKET_AI_REPORT_CREATED, onCreated);
		return () => window.removeEventListener(TICKET_AI_REPORT_CREATED, onCreated);
	}, [ticketId, fetchReports, onReportsChange]);

	return (
		<div
			className={clsx(
				"flex h-full min-h-0 flex-col overflow-y-auto",
				tailwickLayout ? "tailwick-ai-reports-scroll px-3 py-3" : "p-3"
			)}
		>
			{loading ? (
				<div className="flex flex-1 items-center justify-center py-16">
					<CircularProgress size={28} />
				</div>
			) : reports.length === 0 ? (
				<div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-16 text-center">
					<p className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
						{i18n.t("messagesInput.aiReport.emptyTitle")}
					</p>
					<p className="m-0 max-w-md text-sm text-default-500 dark:text-default-400">
						{i18n.t("messagesInput.aiReport.emptyHint")}
					</p>
				</div>
			) : (
				<div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
					{reports.map((report) => (
						<ReportCard
							key={report.id}
							report={report}
							timeZone={businessTimeZone}
						/>
					))}
				</div>
			)}
		</div>
	);
}
